import React, { useState, useRef } from 'react';
import {
  X,
  LogIn,
  UserPlus,
  Mail,
  Lock,
  User as UserIcon,
  Sparkles,
  ShieldCheck,
  Zap,
  Image as ImageIcon,
  ChevronDown,
  Users,
  CheckCircle2,
  AlertCircle,
  Gamepad2,
  Camera,
  Upload,
  RefreshCw,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useRobloky } from '../../context/RoblokyContext';
import { sound } from '../../utils/audio';
import { compressImageFile, formatFileSize } from '../../utils/imageCompressor';
import { basePrefix } from '../../utils/format';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialMode?: 'signin' | 'signup' | 'guest';
}

// Preset avatars: real photos shipped in public/avatars/ (same-origin, no
// external hosts). NOTE: the path is built as `${basePrefix()}/avatars/...`
// inside a template literal on purpose â€” the `/robloky/avatars` part is preceded by
// `)` (not a quote), so the portal prefix-rewriter leaves it alone and the
// runtime adds exactly one site prefix (no /robloky/robloky/... doubling).
const presetAvatar = (n: number) => `${basePrefix()}/avatars/${n}.jpg`;

export const GAMER_AVATAR_PRESETS = [
  { id: 'avatar-1', name: 'Avatar 1', url: presetAvatar(1), category: 'Avatar' },
  { id: 'avatar-2', name: 'Avatar 2', url: presetAvatar(2), category: 'Avatar' },
  { id: 'avatar-3', name: 'Avatar 3', url: presetAvatar(3), category: 'Avatar' },
  { id: 'avatar-4', name: 'Avatar 4', url: presetAvatar(4), category: 'Avatar' },
  { id: 'avatar-5', name: 'Avatar 5', url: presetAvatar(5), category: 'Avatar' },
  { id: 'avatar-6', name: 'Avatar 6', url: presetAvatar(6), category: 'Avatar' },
  { id: 'avatar-7', name: 'Avatar 7', url: presetAvatar(7), category: 'Avatar' },
  { id: 'avatar-8', name: 'Avatar 8', url: presetAvatar(8), category: 'Avatar' },
];

export const AuthModal: React.FC<AuthModalProps> = ({
  isOpen,
  onClose,
  initialMode = 'signin',
}) => {
  const {
    signInWithGoogle,
    signInWithEmail,
    signUpWithEmail,
    signInAsGuest,
  } = useAuth();
  const { addToast } = useRobloky();

  const [mode, setMode] = useState<'signin' | 'signup' | 'guest'>(initialMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [tag, setTag] = useState('RED#777');
  const [selectedAvatar, setSelectedAvatar] = useState(GAMER_AVATAR_PRESETS[0].url);
  const [customAvatarUrl, setCustomAvatarUrl] = useState('');
  const [isCompressing, setIsCompressing] = useState(false);
  const [compressionInfo, setCompressionInfo] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isAvatarPickerOpen, setIsAvatarPickerOpen] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  if (!isOpen) return null;

  // Handle mobile / desktop photo upload with real-time compression & square cropping
  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setIsCompressing(true);
      setErrorMessage(null);

      // Auto-compress & square-crop for standard avatar dimensions
      const result = await compressImageFile(file, {
        maxWidth: 360,
        maxHeight: 360,
        quality: 0.82,
        cropSquare: true,
        format: 'image/webp',
      });

      setSelectedAvatar(result.dataUrl);
      setCustomAvatarUrl(result.dataUrl);
      setCompressionInfo(
        `Optimized: ${formatFileSize(result.originalSize)} â‍” ${formatFileSize(result.compressedSize)} (WebP 1:1)`
      );

      sound.playVictory();
      addToast({
        title: 'Avatar Compressed & Ready! ًں“¸',
        message: `Image optimized to ${formatFileSize(result.compressedSize)} for fastest game performance.`,
        type: 'success',
      });
    } catch (err: any) {
      console.error('Image compression failed:', err);
      setErrorMessage('Failed to process image. Please try another photo format.');
    } finally {
      setIsCompressing(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);
    setIsLoading(true);

    try {
      if (mode === 'signin') {
        await signInWithEmail(email.trim(), password);
        addToast({
          title: 'Welcome Back, Gamer! âڑ،',
          message: 'Signed in successfully with your Robloky account.',
          type: 'success',
        });
        sound.playVictory();
        onClose();
      } else if (mode === 'signup') {
        const finalAvatar = customAvatarUrl.trim() || selectedAvatar;
        await signUpWithEmail(
          email.trim(),
          password,
          displayName.trim() || 'RoblokyGamer',
          finalAvatar
        );
        addToast({
          title: 'Account Created! ًںژ‰',
          message: `Welcome to the Robloky network, ${displayName || 'Gamer'}!`,
          type: 'trophy',
        });
        sound.playVictory();
        onClose();
      } else if (mode === 'guest') {
        const finalAvatar = customAvatarUrl.trim() || selectedAvatar;
        await signInAsGuest(displayName.trim() || 'GuestPlayer', finalAvatar);
        addToast({
          title: 'Guest Gamer Session Activated! ًںژ®',
          message: `Playing as ${displayName || 'GuestPlayer'}.`,
          type: 'info',
        });
        sound.playPartyJoin();
        onClose();
      }
    } catch (err: any) {
      console.error('Auth error:', err);
      let message = err.message || 'Authentication failed. Please check credentials.';
      if (
        err.code === 'auth/user-not-found' ||
        err.code === 'auth/wrong-password' ||
        err.code === 'auth/invalid-credential'
      ) {
        message = 'Invalid email or password. Please check your credentials.';
      } else if (err.code === 'auth/email-already-in-use') {
        message = 'This email is already registered. Please sign in instead.';
      } else if (err.code === 'auth/weak-password') {
        message = 'Password must be at least 6 characters long.';
      } else if (err.code === 'auth/invalid-email') {
        message = 'Please enter a valid email address.';
      }
      setErrorMessage(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setErrorMessage(null);
      setIsLoading(true);
      await signInWithGoogle();
      addToast({
        title: 'Google Sign-In Verified!',
        message: 'Synchronized with Firebase Authentication and Cloud SQL.',
        type: 'success',
      });
      sound.playVictory();
      onClose();
    } catch (err: any) {
      console.error('Google Sign In Error:', err);
      setErrorMessage(err.message || 'Google Sign-In failed or popup was closed.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/85 backdrop-blur-md animate-in fade-in">
      <div className="w-full max-w-lg rounded-2xl border border-cyan-500/40 bg-[#0c101a] p-5 sm:p-6 shadow-[0_0_50px_rgba(6,182,212,0.25)] relative max-h-[92vh] overflow-y-auto">
        
        {/* Close Button */}
        <button
          onClick={() => {
            sound.playClick();
            onClose();
          }}
          className="absolute top-4 right-4 h-8 w-8 rounded-lg bg-slate-900/80 border border-white/10 text-slate-400 hover:text-white flex items-center justify-center transition-colors"
        >
          <X className="h-4 w-4" />
        </button>

        {/* Modal Header */}
        <div className="mb-5">
          <div className="flex items-center gap-2 text-cyan-400 font-mono text-xs font-bold uppercase tracking-wider mb-1">
            <Sparkles className="h-4 w-4 text-cyan-400" />
            <span>ROBLOKY AUTH GATEWAY</span>
          </div>
          <h2 className="text-xl sm:text-2xl font-bold text-white tracking-tight">
            {mode === 'signin' && 'Sign In to Robloky'}
            {mode === 'signup' && 'Create Gamer Account'}
            {mode === 'guest' && 'Quick Guest Play'}
          </h2>
          <p className="text-xs text-slate-400 mt-1">
            {mode === 'signin' && 'Access your persistent gamer profile, Cloud SQL squads, and custom game posts.'}
            {mode === 'signup' && 'Register with real email, password, and custom mobile photo.'}
            {mode === 'guest' && 'Instant access to browsing, testing, and posting without email.'}
          </p>
        </div>

        {/* Auth Mode Switcher Tabs */}
        <div className="grid grid-cols-3 gap-1.5 p-1 bg-slate-950 rounded-xl border border-white/10 mb-5">
          <button
            type="button"
            onClick={() => {
              sound.playClick();
              setMode('signin');
              setErrorMessage(null);
            }}
            className={`py-2 text-xs font-mono font-bold rounded-lg flex items-center justify-center gap-1.5 transition-all ${
              mode === 'signin'
                ? 'bg-cyan-500 text-slate-950 shadow-sm'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <LogIn className="h-3.5 w-3.5" />
            <span>Sign In</span>
          </button>

          <button
            type="button"
            onClick={() => {
              sound.playClick();
              setMode('signup');
              setErrorMessage(null);
            }}
            className={`py-2 text-xs font-mono font-bold rounded-lg flex items-center justify-center gap-1.5 transition-all ${
              mode === 'signup'
                ? 'bg-cyan-500 text-slate-950 shadow-sm'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <UserPlus className="h-3.5 w-3.5" />
            <span>Sign Up</span>
          </button>

          <button
            type="button"
            onClick={() => {
              sound.playClick();
              setMode('guest');
              setErrorMessage(null);
            }}
            className={`py-2 text-xs font-mono font-bold rounded-lg flex items-center justify-center gap-1.5 transition-all ${
              mode === 'guest'
                ? 'bg-purple-600 text-white shadow-sm'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            <Zap className="h-3.5 w-3.5" />
            <span>Guest Pass</span>
          </button>
        </div>

        {/* Error Alert */}
        {errorMessage && (
          <div className="p-3 mb-4 rounded-xl bg-rose-500/15 border border-rose-500/40 text-rose-300 text-xs font-mono flex items-start gap-2 animate-in fade-in">
            <AlertCircle className="h-4 w-4 text-rose-400 shrink-0 mt-0.5" />
            <span>{errorMessage}</span>
          </div>
        )}

        {/* Primary Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          
          {/* Sign Up / Guest: Gamer Handle & Avatar Picker */}
          {(mode === 'signup' || mode === 'guest') && (
            <div className="space-y-3.5 p-3.5 rounded-xl bg-slate-950/60 border border-white/5">
              <div className="flex items-center justify-between">
                <label className="text-xs font-mono font-bold text-cyan-300 flex items-center gap-1.5">
                  <Camera className="h-3.5 w-3.5 text-cyan-400" />
                  <span>Choose or Upload Avatar</span>
                </label>
                {compressionInfo && (
                  <span className="text-[10px] font-mono text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded border border-emerald-500/30">
                    {compressionInfo}
                  </span>
                )}
              </div>

              {/* Upload from Mobile Phone / Device Button */}
              <div className="flex items-center gap-3">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handlePhotoUpload}
                  accept="image/*"
                  className="hidden"
                />
                
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isCompressing}
                  className="flex-1 py-2.5 px-3 rounded-xl bg-cyan-500/15 hover:bg-cyan-500/25 border border-cyan-500/40 text-cyan-300 text-xs font-mono font-bold flex items-center justify-center gap-2 transition-all group"
                >
                  {isCompressing ? (
                    <>
                      <RefreshCw className="h-4 w-4 animate-spin text-cyan-400" />
                      <span>Compressing Image...</span>
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 text-cyan-400 group-hover:-translate-y-0.5 transition-transform" />
                      <span>Upload from Phone / Camera (Auto-Compress)</span>
                    </>
                  )}
                </button>

                {/* Selected Preview Bubble */}
                <div className="h-10 w-10 rounded-xl overflow-hidden border-2 border-cyan-400 shrink-0 bg-black">
                  <img
                    src={customAvatarUrl || selectedAvatar}
                    alt="Preview"
                    className="h-full w-full object-cover"
                  />
                </div>
              </div>

              {/* Current Avatar (large, always visible) */}
              <div className="flex items-center gap-3 pt-1">
                <div
                  className={`h-16 w-16 rounded-2xl overflow-hidden border-2 shrink-0 bg-black transition-all ${
                    customAvatarUrl ? 'border-cyan-400 ring-2 ring-cyan-400/50' : 'border-white/15'
                  }`}
                >
                  <img
                    src={customAvatarUrl || selectedAvatar}
                    alt="Selected avatar"
                    className="h-full w-full object-cover"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => {
                    sound.playClick();
                    setIsAvatarPickerOpen((v) => !v);
                  }}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 border border-white/10 text-slate-300 hover:text-white text-[11px] font-mono font-bold transition-colors"
                >
                  <Users className="h-3.5 w-3.5 text-cyan-400" />
                  <span>{isAvatarPickerOpen ? 'Hide Avatars' : `Browse ${GAMER_AVATAR_PRESETS.length} Presets`}</span>
                  <ChevronDown
                    className={`h-3.5 w-3.5 transition-transform duration-200 ${isAvatarPickerOpen ? 'rotate-180' : ''}`}
                  />
                </button>
              </div>

              {/* Collapsible Avatar Selector Grid */}
              {isAvatarPickerOpen && (
                <div className="grid grid-cols-4 sm:grid-cols-8 gap-2 pt-1 p-2 rounded-xl bg-slate-950/60 border border-white/5 animate-in fade-in slide-in-from-top-1">
                  {GAMER_AVATAR_PRESETS.map((item) => {
                    const isSelected = (!customAvatarUrl && selectedAvatar === item.url);
                    return (
                      <button
                        key={item.id}
                        type="button"
                        title={item.name}
                        onClick={() => {
                          sound.playClick();
                          setSelectedAvatar(item.url);
                          setCustomAvatarUrl('');
                          setCompressionInfo(null);
                        }}
                        className={`relative aspect-square rounded-xl overflow-hidden border-2 transition-all group ${
                          isSelected
                            ? 'border-cyan-400 ring-2 ring-cyan-400/50 scale-105 shadow-md'
                            : 'border-white/10 hover:border-white/40 opacity-70 hover:opacity-100'
                        }`}
                      >
                        <img
                          src={item.url}
                          alt={item.name}
                          className="w-full h-full object-cover"
                        />
                        {isSelected && (
                          <div className="absolute inset-0 bg-cyan-500/20 flex items-center justify-center">
                            <CheckCircle2 className="h-3.5 w-3.5 text-cyan-300 drop-shadow" />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Gamer Name & Tag */}
              <div className="grid grid-cols-2 gap-3 pt-1">
                <div>
                  <label className="block text-[11px] font-mono text-slate-300 mb-1">
                    Gamer Name *
                  </label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. ShadowViper"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-black/60 border border-white/10 text-white text-xs font-bold focus:border-cyan-400 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-[11px] font-mono text-slate-300 mb-1">
                    Gamer Tag #
                  </label>
                  <input
                    type="text"
                    placeholder="RED#777"
                    value={tag}
                    onChange={(e) => setTag(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-black/60 border border-white/10 text-white text-xs font-mono focus:border-cyan-400 focus:outline-none"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Email & Password (Sign In & Sign Up only) */}
          {(mode === 'signin' || mode === 'signup') && (
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-mono text-slate-300 mb-1">
                  Email Address *
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-2.5 h-4 w-4 text-slate-500" />
                  <input
                    type="email"
                    required
                    placeholder="gamer@domain.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-slate-950 border border-white/10 text-white text-xs placeholder:text-slate-600 focus:border-cyan-400 focus:outline-none transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-mono text-slate-300 mb-1">
                  Password *
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-2.5 h-4 w-4 text-slate-500" />
                  <input
                    type="password"
                    required
                    minLength={6}
                    placeholder="â€¢â€¢â€¢â€¢â€¢â€¢â€¢â€¢"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-9 pr-3 py-2.5 rounded-xl bg-slate-950 border border-white/10 text-white text-xs placeholder:text-slate-600 focus:border-cyan-400 focus:outline-none transition-colors"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Action Submit Button */}
          <button
            type="submit"
            disabled={isLoading || isCompressing}
            className="w-full py-3 rounded-xl bg-cyan-500 hover:bg-cyan-400 disabled:opacity-50 text-slate-950 font-bold font-mono text-sm tracking-wider uppercase shadow-[0_0_20px_rgba(6,182,212,0.4)] flex items-center justify-center gap-2 transition-all mt-2"
          >
            {isLoading ? (
              <>
                <div className="h-4 w-4 border-2 border-slate-950 border-t-transparent rounded-full animate-spin" />
                <span>Processing...</span>
              </>
            ) : (
              <>
                {mode === 'signin' && <LogIn className="h-4 w-4" />}
                {mode === 'signup' && <UserPlus className="h-4 w-4" />}
                {mode === 'guest' && <Zap className="h-4 w-4" />}
                <span>
                  {mode === 'signin' && 'Sign In'}
                  {mode === 'signup' && 'Create Account'}
                  {mode === 'guest' && 'Launch Guest Pass'}
                </span>
              </>
            )}
          </button>
        </form>

        {/* Divider */}
        <div className="relative my-5">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-white/10" />
          </div>
          <div className="relative flex justify-center text-[10px] font-mono uppercase">
            <span className="bg-[#0c101a] px-3 text-slate-500">OR CONNECT VIA</span>
          </div>
        </div>

        {/* Google Authentication Button */}
        <button
          type="button"
          onClick={handleGoogleSignIn}
          disabled={isLoading}
          className="w-full py-2.5 px-4 rounded-xl bg-slate-900/90 hover:bg-slate-800 border border-white/15 text-slate-200 text-xs font-mono font-bold flex items-center justify-center gap-2.5 transition-all shadow-sm group"
        >
          <svg className="h-4 w-4" viewBox="0 0 24 24">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
            />
          </svg>
          <span className="group-hover:text-white transition-colors">Continue with Google</span>
        </button>

      </div>
    </div>
  );
};
